import React from 'react';
import AcUnitRounded from '@mui/icons-material/AcUnitRounded';
import AirRounded from '@mui/icons-material/AirRounded';
import CloudQueueRounded from '@mui/icons-material/CloudQueueRounded';
import CloudRounded from '@mui/icons-material/CloudRounded';
import GrainRounded from '@mui/icons-material/GrainRounded';
import ThunderstormRounded from '@mui/icons-material/ThunderstormRounded';
import UmbrellaRounded from '@mui/icons-material/UmbrellaRounded';
import WaterDropRounded from '@mui/icons-material/WaterDropRounded';
import WbCloudyRounded from '@mui/icons-material/WbCloudyRounded';
import WbSunnyRounded from '@mui/icons-material/WbSunnyRounded';
import dayjs from 'dayjs';
import Notice from 'components/common/Notice/Notice';
import TSCard from 'components/common/TSCard/TSCard';
import { useGetTrailWeatherQuery } from 'app/api/trailApiSlice';
import './WeatherForecast.module.scss';

// Each kind of sky takes its icon and colour from the design system: sun in
// ochre, cloud and rain in slate blue, storms in amber.
const SKY = {
  sun: [WbSunnyRounded, 'sun'],
  partly: [WbCloudyRounded, 'sun'],
  cloud: [CloudRounded, 'cloud'],
  fog: [CloudQueueRounded, 'cloud'],
  drizzle: [GrainRounded, 'rain'],
  rain: [UmbrellaRounded, 'rain'],
  snow: [AcUnitRounded, 'rain'],
  storm: [ThunderstormRounded, 'storm'],
};

const WEATHER_CODES = {
  0: ['Clear sky', 'sun'],
  1: ['Mainly clear', 'sun'],
  2: ['Partly cloudy', 'partly'],
  3: ['Overcast', 'cloud'],
  45: ['Fog', 'fog'],
  48: ['Depositing rime fog', 'fog'],
  51: ['Light drizzle', 'drizzle'],
  53: ['Moderate drizzle', 'drizzle'],
  55: ['Dense drizzle', 'drizzle'],
  56: ['Light freezing drizzle', 'drizzle'],
  57: ['Dense freezing drizzle', 'drizzle'],
  61: ['Slight rain', 'rain'],
  63: ['Moderate rain', 'rain'],
  65: ['Heavy rain', 'rain'],
  66: ['Light freezing rain', 'rain'],
  67: ['Heavy freezing rain', 'rain'],
  71: ['Slight snow', 'snow'],
  73: ['Moderate snow', 'snow'],
  75: ['Heavy snow', 'snow'],
  77: ['Snow grains', 'snow'],
  80: ['Slight rain showers', 'rain'],
  81: ['Moderate rain showers', 'rain'],
  82: ['Violent rain showers', 'rain'],
  85: ['Slight snow showers', 'snow'],
  86: ['Heavy snow showers', 'snow'],
  95: ['Thunderstorm', 'storm'],
  96: ['Thunderstorm with slight hail', 'storm'],
  99: ['Thunderstorm with heavy hail', 'storm'],
};

const describeSky = (code) => {
  const [label, sky] = WEATHER_CODES[code] ?? ['Unknown conditions', 'cloud'];
  const [Icon, tone] = SKY[sky];
  return { label, Icon, tone };
};

// Guidance-only — see ADR-0007. This never gates or disables Start
// Activity, it just informs the walker before they decide.
const WeatherForecast = ({ trailId }) => {
  const { data, isLoading, isError } = useGetTrailWeatherQuery(trailId, {
    skip: !trailId,
  });

  if (isLoading || !data || data.hasCoordinates === false) {
    return null;
  }

  if (isError || data.success === false) {
    return (
      <Notice tone="info" title="Weather forecast unavailable">
        We couldn't load the forecast for this trail. Check another source
        before you go.
      </Notice>
    );
  }

  return (
    <TSCard icon={<WbSunnyRounded />} title="Weather forecast">
      {data.hasWarning && (
        <Notice tone="warning" title="Severe weather forecast for this trail">
          Check the forecast below before you go. This is guidance only: the
          decision to go is yours.
        </Notice>
      )}
      <ul className="ts-forecast">
        {data.days.map((day) => {
          const { label, Icon, tone } = describeSky(day.weatherCode);
          return (
            <li key={day.date} className="ts-weather">
              <span className="ts-weather__day">
                {dayjs(day.date).format('ddd D MMM')}
              </span>
              <Icon
                className={`ts-weather__icon ts-weather__icon--${tone}`}
                aria-hidden="true"
              />
              <span className="ts-weather__label">{label}</span>
              <span className="ts-weather__temp">
                <strong>{Math.round(day.maxTempC)}°</strong> /{' '}
                {Math.round(day.minTempC)}°
              </span>
              <span className="ts-weather__fact">
                <WaterDropRounded aria-hidden="true" />
                {day.precipitationProbabilityPercent}% rain
              </span>
              <span className="ts-weather__fact">
                <AirRounded aria-hidden="true" />
                {Math.round(day.windSpeedMaxKmh)} km/h
              </span>
              <span className="ts-weather__feels">
                Feels {Math.round(day.feelsLikeMinC)}° to{' '}
                {Math.round(day.feelsLikeMaxC)}°
              </span>
            </li>
          );
        })}
      </ul>
    </TSCard>
  );
};

export default WeatherForecast;
